import React, { useEffect, useContext, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import PageLayout from "@/components/layouts/page/PageLayout";
import BackLink from "@/components/BackLink";
import PageHeader from "@/components/layouts/pageHeaders/PageHeader";
import isEmpty from "lodash/isEmpty";
import { withFormik } from "formik";
import useQuery from "@/helpers/hooks/useQuery";
import { reportService } from "@/shared/services/reportService";
import NoDataFoundBase from "@/components/NoDataFound";
import DirectMailCampaignReportDetail from "./DirectMailCampaignReportDetail";
import DirectMailCampaignReportFilters from "./DirectMailCampaignReportFilters";
import { renderTemplate } from "@/shared/helpers/reportHelper";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import TableChart from "@/components/charts/TableChart";
import useFetcher from "@/helpers/hooks/useFetcher";
import { Button as ButtonBase } from "@mui/material";
import { getDashboardPath, getWidgetHiddenKeys } from "@/helpers/utils";
import { isNationalPlusUpCampaign } from "@/features/campaigns/direct-mail/national-plus-up/utils/nationalPlusUpHelpers";

type DirectMailReportFormValues = {
  start_date?: string;
  end_date?: string;
  location_id?: string;
  group_id?: string;
  channel_id?: string;
  filter?: string;
  customRange?:
    | {
        from: { year: string | number; month: string | number };
        to: { year: string | number; month: string | number };
      }
    | string;
};

type DirectMailCampaignReportPageProps = {
  values: DirectMailReportFormValues;
  errors: Record<string, unknown>;
  handleChange: any;
  setFieldValue: (_field: string, _value: any) => void;
};

type ParticipantData = {
  id?: string | number;
  system_generated_participant_id?: string;
};

type LoggedInUser = {
  id?: string | number;
  fk_role_id?: string | number;
  participantData?: ParticipantData;
};

type SelectedBrand = {
  id?: string | number;
};

const NoDataFound = NoDataFoundBase as React.ComponentType<any>;
const Button = ButtonBase as React.ComponentType<any>;
const legacyWithFormik = withFormik as any;

const DirectMailCampaignReportPage = ({
  values,
  errors,
  handleChange,
  setFieldValue,
}: DirectMailCampaignReportPageProps) => {
  const { selectedBrand, loggedInUser } = useContext(GlobalContext) as {
    selectedBrand?: SelectedBrand;
    loggedInUser?: LoggedInUser;
  };
  const loader = useContext(LoaderContext) as (_isLoading: boolean) => void;
  const { campaignSysId } = useParams();
  const { locationID } = useQuery();
  const [contentLoaded, setContentLoaded] = useState(false);

  const [campaignReport, setCampaignReport] = useState(null);

  const campaignReportRequestData = useMemo(() => {
    return {
      system_generated_campaign_id: `${campaignSysId}`,
      fk_brand_id: selectedBrand?.id,
      fk_participant_id: loggedInUser?.participantData
        ? loggedInUser?.participantData?.id
        : 0,
      participant_system_id: loggedInUser?.participantData
        ? loggedInUser?.participantData?.system_generated_participant_id
        : "",
      userId: loggedInUser?.id,
      fk_role_id: loggedInUser?.fk_role_id,
      group_id: values.group_id ? values.group_id : "",
      location_id: values.location_id ? values.location_id : "",
      channel_id: values.channel_id ? values.channel_id : "",
      filter: values.filter,
      start_period:
        values.filter === "custom" &&
        values.customRange &&
        typeof values.customRange !== "string"
          ? `${values.customRange.from.year}-${values.customRange.from.month}`
          : "",
      end_period:
        values.filter === "custom" &&
        values.customRange &&
        typeof values.customRange !== "string"
          ? `${values.customRange.to.year}-${values.customRange.to.month}`
          : "",
    };
  }, [selectedBrand?.id, loggedInUser, campaignSysId, values]);

  const {
    data: report,
    isLoading: loading,
    isValidating,
  } = useFetcher(
    Object.values(errors).length === 0 ||
      Object.values(errors).every((v) => v === "")
      ? campaignReportRequestData
      : null,
    reportService.getPostcardCampaignReport,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    if (locationID) {
      setFieldValue("location_id", locationID);
    }
  }, [locationID, setFieldValue]);

  useEffect(() => {
    loader(loading || isValidating);
  }, [loading, loader, isValidating]);

  useEffect(() => {
    if (!loading) {
      setContentLoaded(true);
    }
    if (!isEmpty(report?.data)) {
      setCampaignReport(report?.data);
    }
  }, [loading, report?.data]);

  const reportData =
    !loading && !isEmpty(report?.data) ? report?.data : campaignReport;

  if ((!reportData || isEmpty(reportData)) && !loading) {
    return (
      <NoDataFound
        message={
          <div>
            <div>No Data found!</div>
            <Button
              component={Link}
              to={`${getDashboardPath()}`}
              variant="contained"
              color="primary"
              className="my-4"
              size="large"
            >
              Back to dashboard
            </Button>
          </div>
        }
      />
    );
  }

  const renderTable = (widget) => {
    if (!widget.data || widget.data.length === 0) {
      return <NoDataFound message="There is no record" height="100%" />;
    }
    if (Array.isArray(widget.data)) {
      const headings = Object.keys(widget.data[0]);
      const localHiddenKeys = [
        "creative_image",
        "creative_image_two",
        "file_key",
      ];

      if (isNationalPlusUpCampaign(reportData)) {
        localHiddenKeys.push(
          "file",
          "file_url",
          "address_file",
          "template",
          "template_file"
        );
      }
      const hiddenKeys = getWidgetHiddenKeys(widget, localHiddenKeys);

      const tableFilterKey = `${values.location_id || ""}-${values.group_id || ""}-${values.channel_id || ""}`;
      return (
        <TableChart
          data={widget.data}
          headings={headings}
          formats={widget?.format}
          hiddenKeys={hiddenKeys}
          tableFilterKey={tableFilterKey}
        />
      );
    }
    return null;
  };

  return (
    <>
      <PageLayout className="campaign-report-container mx-0 mx-sm-2">
        <BackLink />
        <PageHeader title="Direct Mail Report" showLastUpdate />
        {contentLoaded ? (
          <div id="campaignReport" className="report campaign-report mt-0 mb-5">
            <div className="card campaign-detail">
              <div className="card-body">
                <DirectMailCampaignReportDetail
                  campaign={reportData}
                  values={values}
                />
              </div>
            </div>
            <DirectMailCampaignReportFilters
              handleChange={handleChange}
              values={values}
              campaign={report?.data}
              setFieldValue={setFieldValue}
            />
            {renderTemplate({
              report: reportData,
              renderTable,
            })}
          </div>
        ) : (
          <NoDataFound message="Loading..." />
        )}
      </PageLayout>
    </>
  );
};

export default legacyWithFormik({
  mapPropsToValues: () => {
    return {
      start_date: "",
      end_date: "",
      location_id: "",
      filter: "custom",
      customRange: "",
    };
  },
  enableReinitialize: true,
  handleSubmit: () => {},
})(DirectMailCampaignReportPage);
